import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ModuleState } from '@redux/modules/types'

import type { BrandCustomProfileModel } from '@models/types'

const watchEntityKeys = ['brandCustomProfiles']

type brandCustomProfileFilters = {
  brand_id?: number,
  profile_type?: string,
  role_id?: number,
}

type UseBrandCustomProfileOptions = {
  entityKey?: string,
  filters?: brandCustomProfileFilters,
  performHttpRequests?: boolean,
  requestOptions?: {
    [key: string]: boolean,
  },
}

function useBrandCustomProfiles(options: UseBrandCustomProfileOptions = {}) {
  const { entityKey, filters = {}, requestOptions } = options
  const {
    brand_id: filterBrandId,
    role_id: filterRoleId,
    profile_type: filterType,
  } = filters

  const {
    updatedEntities: { brandCustomProfiles: brandCustomProfilesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  // Ensure correct typing for selector
  const { brandCustomProfiles } = useSelector(reduxState => reduxState.entities)

  const filteredBrandCustomProfiles = useMemo((): BrandCustomProfileModel[] => {
    const filtered = Object.values(brandCustomProfiles).filter((brandCustomProfile: BrandCustomProfileModel) => {
      const { brand_id, role_id, profile_type } = brandCustomProfile

      const matchBrand = matchFilterNumber(brand_id, filterBrandId)
      const matchRole = matchFilterNumber(role_id, filterRoleId)

      const matchFilterType = matchFilterString(profile_type, filterType)

      return matchBrand && matchRole && matchFilterType
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [brandCustomProfilesUpdatedAt, JSON.stringify(options)])

  const filteredBrandCustomProfilesCount = filteredBrandCustomProfiles.length
  const hasFilteredBrandCustomProfiles = !!filteredBrandCustomProfilesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredBrandCustomProfilesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingBrandCustomProfiles } = useReduxAction(
    'brandCustomProfiles',
    'loadBrandCustomProfiles',
    {
      entityKey,
      ...filtersWithOffset,
      ...requestOptions,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredBrandCustomProfiles,
    filteredBrandCustomProfilesCount,
    hasFilteredBrandCustomProfiles,
    loading: loadingBrandCustomProfiles,
  }
}

export default useBrandCustomProfiles
